// Mapping dos tiers de Premium do Cabal Liberty.
// Backend retorna `serviceKind` em PremiumInfo — aqui convertemos
// em nome, cor e icone consistentes com a identidade Liberty.
//
// Hierarquia (definida pelo servidor do jogo):
//   1 = melhor  -> Platina   (Cabal Next, dono 2026-10-17; no Liberty era Lendário)
//   2 = medio   -> Prata     (Cabal Next; no Liberty era Mítico)
//   3 = basico  -> Épico     (roxo)
//   0 = free    -> Free      (cinza, default quando nao ha premium)
// A conta do Next tem o principal (ServiceKind) + o adicional (AdditionalServiceKind): premiumLabel junta os dois.
//
// Pra trocar nomes/cores, basta editar este arquivo — todo o site le daqui.

export type PremiumTier = {
  serviceKind: number;
  name: string;
  shortName: string;   // usado em badges estreitos
  color: string;       // cor primaria do tier
  glow: string;        // cor do glow (box-shadow)
  bgSoft: string;      // bg translucido (badges/cards)
  borderSoft: string;  // border translucida
  description: string;
  isPaid: boolean;
};

const DEFAULT_FREE: PremiumTier = {
  serviceKind: 0,
  name: 'Free',
  shortName: 'Free',
  color: '#64748b',
  glow: 'rgba(100, 116, 139, 0.25)',
  bgSoft: 'rgba(100, 116, 139, 0.10)',
  borderSoft: 'rgba(100, 116, 139, 0.25)',
  description: 'Acesso gratuito ao jogo.',
  isPaid: false,
};

export const PREMIUM_TIERS: Record<number, PremiumTier> = {
  1: {
    serviceKind: 1,
    name: 'Platina',
    shortName: 'Platina',
    color: '#DDE7F3',
    glow: 'rgba(221, 231, 243, 0.35)',
    bgSoft: 'rgba(221, 231, 243, 0.12)',
    borderSoft: 'rgba(221, 231, 243, 0.35)',
    description: 'Plano definitivo — todos os beneficios com o maximo de bonus.',
    isPaid: true,
  },
  2: {
    serviceKind: 2,
    name: 'Prata',
    shortName: 'Prata',
    color: '#B8C0CC',
    glow: 'rgba(184, 192, 204, 0.35)',
    bgSoft: 'rgba(184, 192, 204, 0.12)',
    borderSoft: 'rgba(184, 192, 204, 0.35)',
    description: 'Plano intermediario — bonus fortes com excelente custo-beneficio.',
    isPaid: true,
  },
  3: {
    serviceKind: 3,
    name: 'Épico',
    shortName: 'Épico',
    color: '#A855F7',
    glow: 'rgba(168, 85, 247, 0.35)',
    bgSoft: 'rgba(168, 85, 247, 0.12)',
    borderSoft: 'rgba(168, 85, 247, 0.35)',
    description: 'Plano de entrada — os recursos essenciais premium.',
    isPaid: true,
  },
  0: DEFAULT_FREE,
};

/** Resolve um serviceKind em tier. Caso invalido, retorna Free. */
export function getPremiumTier(serviceKind?: number | null): PremiumTier {
  if (!serviceKind) return DEFAULT_FREE;
  return PREMIUM_TIERS[serviceKind] ?? DEFAULT_FREE;
}

/** Tier "ativo" considerando premium expirado = Free. */
export function getActiveTier(
  serviceKind?: number | null,
  daysLeft?: number
): PremiumTier {
  if (!serviceKind || !daysLeft || daysLeft <= 0) return DEFAULT_FREE;
  return getPremiumTier(serviceKind);
}

const daysUntil = (date?: string | null) =>
  date ? Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 86400000)) : 0;

/** Cabal Next (dono 2026-10-17): principal + adicional ativos -> "Platina/Prata" (melhor primeiro); um so' = o nome
 *  dele; nenhum = Free. days = o que dura mais. */
export function premiumLabel(premium?: {
  serviceKind?: number | null;
  expireDate?: string | null;
  additionalServiceKind?: number | null;
  additionalExpireDate?: string | null;
} | null): { name: string; days: number; isPaid: boolean } {
  const active: { kind: number; days: number }[] = [];
  const mainDays = daysUntil(premium?.expireDate);
  if (premium?.serviceKind && mainDays > 0) active.push({ kind: premium.serviceKind, days: mainDays });
  const addDays = daysUntil(premium?.additionalExpireDate);
  if (premium?.additionalServiceKind && addDays > 0) active.push({ kind: premium.additionalServiceKind, days: addDays });
  const kinds = [...new Set(active.map((a) => a.kind))].sort((a, b) => a - b);
  if (!kinds.length) return { name: DEFAULT_FREE.name, days: 0, isPaid: false };
  return {
    name: kinds.map((k) => getPremiumTier(k).name).join('/'),
    days: Math.max(...active.map((a) => a.days)),
    isPaid: true,
  };
}
